import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export const money = (n, cur = "INR") => (n === null || n === undefined ? "Unknown" : new Intl.NumberFormat("en-IN", { style: "currency", currency: cur || "INR", maximumFractionDigits: 2 }).format(n));
export const label = (s) => (s || "").replace(/_/g, " ");
const TONE = {
  draft: "bg-slate-100 text-slate-700", pending_verification: "bg-amber-100 text-amber-800", active: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-orange-100 text-orange-800", inactive: "bg-slate-200 text-slate-600", submitted: "bg-sky-100 text-sky-800",
  approved: "bg-indigo-100 text-indigo-800", rejected: "bg-rose-100 text-rose-700", cancelled: "bg-slate-200 text-slate-600",
  closed: "bg-slate-200 text-slate-700", verified: "bg-emerald-100 text-emerald-800", requested: "bg-amber-100 text-amber-800",
  executed: "bg-sky-100 text-sky-800", confirmed: "bg-emerald-100 text-emerald-800", reversed: "bg-rose-100 text-rose-700",
  paid: "bg-emerald-100 text-emerald-800", partially_paid: "bg-amber-100 text-amber-800", unpaid: "bg-slate-100 text-slate-700",
  open: "bg-amber-100 text-amber-800", in_progress: "bg-sky-100 text-sky-800", resolved: "bg-emerald-100 text-emerald-800",
  high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-700",
};
export const Badge = ({ s, testid }) => <span data-testid={testid} className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${TONE[s] || "bg-slate-100 text-slate-700"}`}>{label(s)}</span>;

export function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`rounded-2xl border border-pawkt-border bg-white p-4 ${className}`}>{children}</div>;
}

// Promise-based reason/confirm prompt: const [ask, askNode] = useAsk(); const r = await ask("Title", "help");
export function useAsk() {
  const [st, setSt] = useState(null);
  const [val, setVal] = useState("");
  const ask = (title, help = "", placeholder = "Reason") => new Promise((resolve) => { setVal(""); setSt({ title, help, placeholder, resolve }); });
  const close = (v) => { st?.resolve(v); setSt(null); };
  const node = (
    <Dialog open={!!st} onOpenChange={(o) => !o && close(null)}>
      <DialogContent className="bg-white max-w-md" data-testid="fin-ask-dialog">
        <DialogHeader><DialogTitle>{st?.title}</DialogTitle>{st?.help && <DialogDescription>{st.help}</DialogDescription>}</DialogHeader>
        <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)} placeholder={st?.placeholder} data-testid="fin-ask-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => close(null)} data-testid="fin-ask-cancel">Cancel</Button>
          <Button className="bg-slate-900 text-white" disabled={!val.trim()} onClick={() => close(val.trim())} data-testid="fin-ask-ok">Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return [ask, node];
}

const RETRY = {
  POSSIBLE_DUPLICATE: ["duplicate_reason", "Possible duplicate vendor"],
  POSSIBLE_DUPLICATE_INVOICE: ["duplicate_reason", "Possible duplicate invoice"],
  POSSIBLE_DUPLICATE_PAYMENT: ["duplicate_reason", "Possible duplicate payment"],
  SOD_REASON_REQUIRED: ["override_reason", "Owner override"],
};

// POST with the server's confirm-with-reason flows (duplicates, owner SoD override). Returns data or null.
export async function finPost(ask, url, body = {}, okMsg) {
  try {
    const r = (await api.post(url, body)).data;
    if (okMsg) toast.success(okMsg);
    return r;
  } catch (e) {
    const d = e?.response?.data?.detail;
    const rt = d && RETRY[d.code];
    if (rt) {
      const extra = d.duplicates || d.risks || d.existing;
      const why = Array.isArray(extra) ? ` Matches: ${extra.map((x) => x.legal_name || x.number || x.vendor_invoice_no).join(", ")}.` : "";
      const reason = await ask(rt[1], `${d.msg}${why}`);
      if (!reason) return null;
      return finPost(ask, url, { ...body, [rt[0]]: reason }, okMsg);
    }
    toast.error(apiErr(e));
    return null;
  }
}

export function Field({ label: l, children, className = "" }) {
  return <label className={`block text-xs font-medium text-slate-600 ${className}`}>{l}<div className="mt-1">{children}</div></label>;
}

export const sel = "h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";

export function useBranchList(user) {
  return useBranchQuery(user).list;
}
function useBranchQuery(user) {
  const { data, isLoading } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data, staleTime: 300e3 });
  const scope = user?.branch_ids?.length ? new Set(user.branch_ids) : null;
  return { list: (data || []).filter((b) => !scope || scope.has(b.id)), isLoading };
}
// true when the user must pick a branch before saving (2+ branches available, none chosen); also true while branches load
export const useBranchMissing = (user, value) => {
  const { list, isLoading } = useBranchQuery(user);
  return isLoading || (list.length > 1 && !value);
};

// Branch picker for payables forms; hidden when the business has one branch (server auto-assigns).
export function BranchField({ value, onChange, user }) {
  const list = useBranchList(user);
  if (list.length < 2) return null;
  return (
    <Field label="Branch *"><select className={sel} value={value} onChange={(e) => onChange(e.target.value)} data-testid="fin-branch">
      <option value="">Choose…</option>{list.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
      {!value && <span className="mt-1 block text-[11px] text-amber-700" data-testid="fin-branch-hint">Choose a branch to save</span>}</Field>
  );
}

export async function finSend(method, url, body, okMsg) {
  try { const r = (await api[method](url, body)).data; if (okMsg) toast.success(okMsg); return r || true; }
  catch (e) { toast.error(apiErr(e)); return null; }
}

export function Docs({ base, docs, onUploaded, canUpload, testid, canRemove, ask }) {
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document attached"); onUploaded?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const open = async (d) => {
    try { const r = await api.get(`${base}/${d.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid={testid}>
      <ul className="space-y-1 text-sm">{(docs || []).map((d) => <li key={d.id} data-testid={`${testid}-item-${d.id}`}>{d.removed
        ? <span className="text-xs text-slate-400 line-through" title={d.removed_reason}>{d.label || d.id}</span>
        : <button className="text-left text-sky-700 underline" onClick={() => open(d)}>{d.label || d.id}</button>} <span className="text-xs text-slate-400">{d.uploaded_by}{d.removed ? ` · removed by ${d.removed_by}: ${d.removed_reason}` : ""}</span>
        {canRemove && !d.removed && <button className="ml-2 text-xs text-rose-600 underline" data-testid={`${testid}-remove-${d.id}`} onClick={async () => {
          const r = await ask("Remove document", "The file is kept for the audit trail but no longer shown as current evidence. Give a reason.");
          if (r && await finSend("post", `${base}/${d.id}/remove`, { reason: r }, "Document removed")) onUploaded?.();
        }}>remove</button>}</li>)}
        {!docs?.length && <li className="text-xs text-slate-400">No documents yet.</li>}</ul>
      {canUpload && <label className="mt-2 inline-flex cursor-pointer items-center rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
        {busy ? "Uploading…" : "Attach PDF / image"}<input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => up(e.target.files?.[0])} data-testid={`${testid}-input`} /></label>}
    </div>
  );
}

export const graceMine = (g, user) => !!g?.active && g.eligible !== false && g.creator_id === user?.id;

export function GraceNote({ g, user, creator, testid = "grace-note" }) {
  if (!g || g.eligible === false || !g.created_at) return null;
  const h = Math.floor(g.seconds_left / 3600), m = Math.floor((g.seconds_left % 3600) / 60);
  const mine = g.creator_id === user?.id;
  return (
    <p className={`rounded-md px-2 py-1.5 text-xs ${g.active && mine ? "bg-sky-50 text-sky-900" : "bg-slate-50 text-slate-600"}`} data-testid={testid}>
      Created by {creator || "—"} on {String(g.created_at).slice(0, 16).replace("T", " ")} UTC.{" "}
      {g.active ? (mine ? <>24-hour grace window: <b>{h}h {m}m left</b>. You can correct or withdraw this record without Owner approval. This doesn't approve, verify or pay it.</> : <>The creator's grace window ends in {h}h {m}m.</>)
        : "Grace window ended; the normal approval rules apply."}
    </p>
  );
}

